import type { ReactNode } from 'react'
import {
  Alert, Badge, Code, Group, Paper, Stack, Table, Text, Skeleton as MSkeleton,
} from '@mantine/core'
import {
  IconAlertTriangle, IconEye, IconEyeOff, IconHelpCircle, IconInfoCircle, IconLock, IconSettingsOff,
} from '@tabler/icons-react'
import {
  PageTitle, SectionCard, StatusBadge, EmptyState, MetaList, HairlineDivider,
} from '@lighthouse/design'
import type { StatusRole } from '@lighthouse/design'
import {
  SILENTLY_OFF_KEYS, apiRunsInDevelopment, crossHostKindLabel, crossHostStatus, crossHostTally,
  groupByTier, hostColumns, hostIdentity, hostLabel, hostStanding, joinOr, observationFor,
  roleLabel, sharedEditabilityNote, sourceLabel, tierDescription, tierTitle, unboundColumns,
  unplacedObservations,
} from '../components/configuration/configurationFormat'
import type { HostColumn } from '../components/configuration/configurationFormat'
import { RestrictedState } from '../components/access/RestrictedState'
import { useHostConfiguration } from '../hooks/useOverview'
import { useLighthouseAdmin } from '../hooks/useLighthouseAdmin'
import { isApiError } from '../services/apiError'
import { NOT_MEASURED, agoLabel, errorKind, errorSummary, whenLabel } from '../utils/format'
import type {
  ConfigurationHost, ConfigurationObservation, ConfigurationOverview, ConfigurationSetting,
  ConfigurationTier,
} from '../types/observatory'

/**
 * Configuration — every allowlisted setting, as each host that binds it reports
 * it. Read-only.
 *
 * Doc 16 §7 is the whole argument for this page: two subsystems — data-quality
 * validation and failure email — are disabled in Production, named in no
 * configuration file, and discoverable only by opening a `.cs` file; and
 * row-level security is off in Staging and Production with no screen saying so.
 * "A page that surfaced only that would be worth building with no editable field
 * at all." This is that page, and it has no editable field.
 *
 * Six decisions shape it:
 *
 *  1. **Per host, because configuration here is per host.** DataQuality and
 *     Notifications are bound in Automaton alone, and in Azure the API and the
 *     worker have separate app-settings arrays — so a page reading the API's own
 *     configuration would report "off" for them meaning only "the API has no
 *     opinion", even on the day someone turns them on where they run. The server
 *     serves every observation with its host, and the page keeps it: hosts are
 *     COLUMNS, so the two readings of one key that legitimately differ — the
 *     outbox is written on the API and drained on Automaton — sit side by side
 *     rather than forty rows apart in two per-host lists. A cell has three
 *     states that must never look alike: a value, a dash (the host binds the key
 *     and has not reported it — unmeasured), and "not bound here" (the host does
 *     not read the key at all).
 *  2. **Never-reported is read FIRST, and it does not empty the page.** Unlike
 *     the workers and sources envelopes, the API's own readings are real
 *     measurements taken from this process, so the page renders; what goes to
 *     dashes is every Automaton cell — including the two settings the page
 *     exists to surface. The banner counts how many settings have no reading
 *     from any host, because "DataQuality: —" is the honest answer on a fresh
 *     database and it must not be mistaken for "DataQuality: false".
 *  3. **The page prints the server's words for every cell.** `valueLabel` is
 *     written server-side from the disposition and never quotes configuration;
 *     Bootstrap settings carry no value on the wire at all. So no helper here
 *     formats a value, and the one place `value` is read at all
 *     (`apiRunsInDevelopment`) chooses wording and prints nothing of it.
 *  4. **Every value is as of process start, and every row says so.** Nothing is
 *     runtime-mutable (`IOptionsSnapshot` appears nowhere in `src/`). The server's
 *     own sentence leads the page, each host column is headed with its start
 *     instant, and each cell's source line reads "read at start from …". A
 *     configuration page implying live values would be lying about when they
 *     took effect.
 *  5. **Read-only, visibly.** No input, no toggle, no switch-shaped control
 *     anywhere — not even a disabled one, which would imply a permission rather
 *     than a decision. Locked rows each say why they are locked; the other two
 *     tiers say it once per section, because their notes are identical down the
 *     tier (measured, not assumed — see `sharedEditabilityNote`).
 *  6. **The rationale is on screen, never in a tooltip.** Doc 16 §7: it is worth
 *     more than any input box, and it is readable today only by opening source.
 *
 * **The gate, and its Development trade.** `LighthouseAdmin` at class level:
 * outside Development this 403s for anyone without `Lighthouse.Admin`, which
 * until that role is provisioned in Entra is everyone. In Development the policy
 * is any authenticated user and the checked-in dev API key satisfies it — which
 * the page says out loud when the API reports it is running in Development.
 *
 * The page keeps the gate contract (hooks/useLighthouseAdmin.ts) as
 * ScheduleLoadPage does: when the server has already said this caller lacks the
 * role, the fetching body is never mounted — `useHostConfiguration` has no
 * `enabled` option, and it polls, so a denied caller who opened the URL directly
 * would otherwise send a 403 every interval for as long as the page stayed open.
 * While the catalogue has not answered, the page waits rather than race it. When
 * the gate answered and cannot say (`unknown`), the body mounts and a 403 of its
 * own renders the same restricted state — an administrator is never locked out.
 */
export function ConfigurationPage() {
  const gate = useLighthouseAdmin()
  if (gate.state === 'denied') return <ConfigurationRestricted reason={gate.reason} />
  if (gate.state === 'pending') return <ConfigurationSkeleton />
  return <ConfigurationFetch />
}

/** The page for a caller the server does not serve configuration to. */
function ConfigurationRestricted({ reason }: { reason?: string | null }) {
  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle={PAGE_SUBTITLE}>Configuration</PageTitle>
      <RestrictedState surface="Configuration" reason={reason} />
    </Stack>
  )
}

/** Loading — the gate's answer, or the configuration itself. Never a page of dashes. */
function ConfigurationSkeleton() {
  return (
    <Stack gap="md" aria-busy="true">
      <MSkeleton height={120} />
      <MSkeleton height={240} />
      <MSkeleton height={420} />
    </Stack>
  )
}

function ConfigurationFetch() {
  const { data, isLoading, isError, error } = useHostConfiguration()

  // A 403 is a missing role, not a fault: the restricted state, never the error panel.
  if (isError && errorKind(error) === 'forbidden') {
    return <ConfigurationRestricted reason={isApiError(error) ? error.detail : null} />
  }
  if (isError) return <ConfigurationError error={error} />

  // `|| !data` is load-bearing: between React Query's retries the flags read
  // "not loading, not errored" with no payload, and a page that renders then
  // has only its own defaults to show. On this page those defaults are dashes
  // and "not bound" — a confident-looking non-answer about every setting.
  if (isLoading || !data) return <ConfigurationSkeleton />

  return <ConfigurationBody overview={data} />
}

const PAGE_SUBTITLE =
  "Every allowlisted setting, as each host that binds it reports it — read-only, and as of each process's start"

function ConfigurationBody({ overview }: { overview: ConfigurationOverview }) {
  const columns = hostColumns(overview.hosts)
  const unmeasured = overview.settings.filter(isUnmeasuredEverywhere)
  const headlineUnmeasured = unmeasured.filter((s) =>
    SILENTLY_OFF_KEYS.some((key) => key.toLowerCase() === s.key.toLowerCase())).length

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle={PAGE_SUBTITLE}>Configuration</PageTitle>

      <ValuesAsOf sentence={overview.valuesAsOf} />
      {apiRunsInDevelopment(overview.settings) && <DevelopmentGate />}

      {/*
        Branch order is load-bearing, not stylistic: the never-reported envelope
        carries `isProjectionStale: false`, so asking about staleness first
        presents an empty projection as a fresh one.
      */}
      {overview.hasNeverReported ? (
        <NeverReported
          unmeasured={unmeasured.length}
          headlineUnmeasured={headlineUnmeasured}
          total={overview.settings.length}
        />
      ) : (
        overview.isProjectionStale && <ProjectionStale observedAt={overview.observedAt} />
      )}

      <HostsPanel hosts={overview.hosts} />
      <SilentlyOffPanel settings={overview.settings} columns={columns} />
      <CrossHostPanel settings={overview.settings} />

      {groupByTier(overview.settings).map((group) => (
        <TierSection key={group.tier} tier={group.tier} settings={group.settings} columns={columns} />
      ))}
    </Stack>
  )
}

/**
 * The request failed — for a reason other than a refusal, which is the restricted
 * state above. Nothing below it is known — including whether anything is off —
 * and the copy says so rather than rendering a page of dashes that would read as
 * a measured absence.
 */
function ConfigurationError({ error }: { error: unknown }) {
  const summary = errorSummary(error)
  return (
    <EmptyState
      icon={<IconSettingsOff size={28} stroke={1.5} />}
      title="Could not load configuration"
      message={
        'The request did not come back, so nothing is known here about any setting — including whether '
        + 'anything is off. Two causes land identically. On a fresh database, migration AddHostConfiguration '
        + 'may not have been applied, since migrations here are applied by hand. And the dev proxy answers 500 '
        + 'when the API is simply not running.'
      }
      action={summary ? <Code style={{ overflowWrap: 'anywhere' }}>{summary}</Code> : undefined}
    />
  )
}

/**
 * The standing sentence about when values took effect, verbatim from the server,
 * plus the read-only statement. Shown in every state, including the healthy one.
 */
function ValuesAsOf({ sentence }: { sentence: string }) {
  return (
    <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" title="As of each process's start, and read-only">
      <Stack gap={4}>
        <Text size="sm">{sentence}</Text>
        <Text size="sm">
          This page has no input and no write path: it reports configuration, it does not change it. Only
          allowlisted settings appear, and a Bootstrap setting shows whether it is set and where it came
          from — never its value.
        </Text>
      </Stack>
    </Alert>
  )
}

/**
 * The trade the controller accepts in Development, said where it applies.
 *
 * Chosen from the API's own served environment, not from this SPA's build mode:
 * the gate is the API's, and a production SPA pointed at a Development API is
 * exactly the case where the reader would not otherwise know.
 */
function DevelopmentGate() {
  return (
    <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" title="This API is running in Development">
      <Text size="sm">
        So the administrator gate on this page is degraded to &ldquo;any authenticated user&rdquo;, and the
        development API key — a constant checked into the repository — satisfies it. Anyone holding the
        repository can read this page locally. That is acceptable only because of what the page can say: no
        Bootstrap value is ever served, and every other value is a parsed flag, number, duration or
        closed-set name.
      </Text>
    </Alert>
  )
}

/** Every host that binds the setting has yet to report it: no reading exists anywhere. */
function isUnmeasuredEverywhere(setting: ConfigurationSetting): boolean {
  return setting.observations.length > 0 && setting.observations.every((o) => o.hasNeverReported)
}

/**
 * Automaton has never published. The API's readings stand; every Automaton cell
 * is a dash, and the dash means unmeasured.
 *
 * Both counts are taken off the payload rather than asserted, so the sentence
 * stays true if the allowlist ever moves a headline setting into a second host.
 */
function NeverReported({
  unmeasured, headlineUnmeasured, total,
}: { unmeasured: number; headlineUnmeasured: number; total: number }) {
  return (
    <Alert icon={<IconHelpCircle size={16} />} color="gray" variant="light" title="Automaton has never reported its configuration">
      <Text size="sm">
        Automaton publishes its allowlisted settings at startup and every ten minutes, and nothing has been
        recorded. Every Automaton cell below is therefore a dash — <strong>unmeasured, not off</strong> —
        and {unmeasured} of {total} settings have no reading from any host at all.
        {headlineUnmeasured > 0 && (
          <>
            {' '}That includes {headlineUnmeasured} of the rows under &ldquo;Silently off&rdquo;: for those,
            this page cannot say whether they are on or off until Automaton reports.
          </>
        )}
        {' '}Either no worker is pointed at this database, or none has started since the projection table
        was created.
      </Text>
    </Alert>
  )
}

/** The newest projected reading is old: the observer stopped, and the cells are history. */
function ProjectionStale({ observedAt }: { observedAt: string | null }) {
  return (
    <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" title="Automaton's readings are history, not status">
      <Text size="sm">
        Automaton last published its configuration {agoLabel(observedAt)}. Its cells below are what it last
        ran with; it may since have stopped, or restarted with different settings. Rules across hosts are
        judged only over hosts reporting now, so while this holds they read &ldquo;not judged&rdquo;.
      </Text>
    </Alert>
  )
}

/**
 * Every host on the page, with the instant its values took effect.
 *
 * This is the column key for everything below. The dashboard is listed even
 * though nothing publishes it, because it binds settings — its column is dashes
 * that say "not observable", which is not the same statement as "not set".
 */
function HostsPanel({ hosts }: { hosts: ConfigurationHost[] }) {
  return (
    <SectionCard
      title="Hosts"
      subtitle="Configuration is per host: each process has its own settings, and in Azure the API and the worker are configured separately"
    >
      <div
        className="lh-overview-grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}
      >
        {hosts.map((host) => {
          const standing = hostStanding(host)
          const identity = hostIdentity(host)
          return (
            <Paper
              key={`${host.role}::${host.hostName ?? ''}`}
              withBorder
              p="sm"
              style={{ backgroundColor: 'var(--color-bg-surface)' }}
            >
              <Stack gap={6}>
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={600}>{roleLabel(host.role)}</Text>
                    <Text size="xs" c="dimmed" ff={identity.isMachineName ? 'monospace' : undefined}>
                      {identity.text}
                    </Text>
                  </div>
                  <Standing {...standing} />
                </Group>
                <MetaList
                  items={[
                    { label: 'Values took effect', value: whenLabel(host.processStartedAt) },
                    {
                      // Not a dash for this instance: it publishes nothing, because
                      // the API reads its own snapshot (taken right after Build(),
                      // not per request — the appsettings files reload on change),
                      // and a dash would suggest a projection that missed a beat.
                      label: 'Last published',
                      value: host.isThisInstance
                        ? 'not projected; read from its startup snapshot'
                        : whenLabel(host.observedAt),
                    },
                  ]}
                />
                <Text size="xs" c="dimmed">{host.note}</Text>
              </Stack>
            </Paper>
          )
        })}
      </div>
    </SectionCard>
  )
}

/**
 * Doc 16 §7's rows, first.
 *
 * Each is shown per host that binds it, with the hosts that do NOT bind it named
 * — because "the API does not read DataQuality" is the precise fact doc 16 missed
 * and the reason this page is per host at all. No colour on any value: whether
 * these being off is intended has not been decided (doc 16 §10), and a red
 * `false` would be this page ruling on it.
 */
function SilentlyOffPanel({ settings, columns }: { settings: ConfigurationSetting[]; columns: HostColumn[] }) {
  return (
    /*
      Two kinds of finding, named as two. Doc 16 §7 lists settings that are
      silently OFF and, separately, settings that are SET but read by no code
      (Jobs:FullEtl / IncrementalEtl are set true in Production and reach a
      runner nothing registers). An earlier heading called all of them
      "disabled", so the two dead-config rows read "set" under a title saying
      they were off: the heading and the cell disagreed about the same row.
    */
    <SectionCard
      title="Silently off, or set and read by nothing — read these first"
      subtitle="The settings doc 16 §7 found either disabled or dead, with nothing on any screen saying so. Whether any of them should change has not been decided; this page reports, it does not rule"
    >
      <Stack gap="md">
        {SILENTLY_OFF_KEYS.map((key, i) => {
          const setting = settings.find((s) => s.key.toLowerCase() === key.toLowerCase())
          return (
            <Stack key={key} gap="xs">
              {i > 0 && <HairlineDivider />}
              {setting ? (
                <HeadlineSetting setting={setting} columns={columns} />
              ) : (
                <NotServed settingKey={key} />
              )}
            </Stack>
          )
        })}
      </Stack>
    </SectionCard>
  )
}

function HeadlineSetting({ setting, columns }: { setting: ConfigurationSetting; columns: HostColumn[] }) {
  const bound = columns.filter((column) => observationFor(setting, column) !== undefined)
  const unbound = unboundColumns(setting, columns)

  return (
    <Stack gap="xs">
      <SettingHeading setting={setting} />
      <Text size="sm">{setting.rationale}</Text>
      <MetaList
        items={bound.map((column) => ({
          label: hostLabel(column.host),
          value: <ObservationCell observation={observationFor(setting, column)} align="right" />,
        }))}
      />
      <UnplacedReadings setting={setting} columns={columns} />
      {unbound.length > 0 && (
        <Text size="xs" c="dimmed">
          Not read by {joinOr(unbound.map((c) => hostLabel(c.host)))} —{' '}
          {unbound.length === 1 ? 'it has no value' : 'none of them has a value'} for it, which is not the
          same as off.
        </Text>
      )}
    </Stack>
  )
}

/**
 * A headline key the server no longer serves.
 *
 * Rendered rather than skipped, so a rename on the allowlist breaks this panel
 * visibly instead of quietly shrinking it — the full list below is the server's
 * and is unaffected either way.
 */
function NotServed({ settingKey }: { settingKey: string }) {
  return (
    <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} title={<Code>{settingKey}</Code>}>
      <Text size="sm">
        Not served. This panel names its keys from doc 16 §7, and the server&rsquo;s allowlist no longer
        includes this one — it has been renamed or removed. Every setting the server does serve is in the
        full list below; this panel&rsquo;s list needs updating in <Code>configurationFormat.ts</Code>.
      </Text>
    </Alert>
  )
}

/**
 * The server's cross-host rules, and their verdicts.
 *
 * Judged server-side over hosts reporting NOW, so a stale or never-reported host
 * makes a rule "not judged" rather than passing it. That is the dash rule applied
 * to agreement: two hosts nobody has heard from do not agree, they are unknown.
 */
function CrossHostPanel({ settings }: { settings: ConfigurationSetting[] }) {
  const checked = settings.filter((s) => s.crossHostCheck != null)
  if (checked.length === 0) return null

  const tally = crossHostTally(checked.map((s) => s.crossHostCheck!))

  return (
    <SectionCard
      title="Rules across hosts"
      subtitle={`Settings that must agree between hosts, or must differ in one exact way. Judged only over hosts reporting now${tally ? ` · ${tally}` : ''}`}
    >
      <Stack gap="md">
        {checked.map((setting, i) => {
          const check = setting.crossHostCheck!
          const verdict = crossHostStatus(check)
          return (
            <Stack key={setting.key} gap={6}>
              {i > 0 && <HairlineDivider />}
              <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
                <div style={{ minWidth: 0 }}>
                  <Code>{setting.key}</Code>
                  <Text size="xs" c="dimmed" mt={2}>{crossHostKindLabel(check.kind)}</Text>
                </div>
                <Standing status={verdict.status} label={verdict.label} isUnmeasured={verdict.status == null} />
              </Group>
              <Text size="sm">{check.detail}</Text>
            </Stack>
          )
        })}
      </Stack>
    </SectionCard>
  )
}

/**
 * One tier, as a setting-by-host matrix.
 *
 * The table scrolls inside its own container rather than the page scrolling
 * sideways, so the banners above stay put on a narrow screen.
 */
function TierSection({
  tier, settings, columns,
}: { tier: ConfigurationTier; settings: ConfigurationSetting[]; columns: HostColumn[] }) {
  const sharedNote = sharedEditabilityNote(settings)

  return (
    <SectionCard
      title={<Group gap={8} wrap="nowrap"><TierIcon tier={tier} />{tierTitle(tier)}</Group>}
      subtitle={`${settings.length} setting${settings.length === 1 ? '' : 's'}. ${tierDescription(tier)}`}
      flush
    >
      {sharedNote && (
        <Text size="xs" c="dimmed" px="md" pt="sm">
          <strong>Not editable here:</strong> {sharedNote}
        </Text>
      )}
      <Table.ScrollContainer minWidth={380 + columns.length * 220}>
        <Table verticalSpacing="sm" horizontalSpacing="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ minWidth: 360 }}>Setting</Table.Th>
              {columns.map((column) => (
                <Table.Th key={column.id} style={{ minWidth: 200 }}>
                  <HostColumnHeading host={column.host} />
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {settings.map((setting) => (
              <Table.Tr key={setting.key}>
                <Table.Td style={{ verticalAlign: 'top' }}>
                  <SettingDescription setting={setting} columns={columns} showNote={sharedNote == null} />
                </Table.Td>
                {columns.map((column) => (
                  <Table.Td key={column.id} style={{ verticalAlign: 'top' }}>
                    <ObservationCell observation={observationFor(setting, column)} />
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </SectionCard>
  )
}

/**
 * A host column's heading: who, which machine, and the instant its values took
 * effect — which is the "as of" every cell beneath it shares.
 */
function HostColumnHeading({ host }: { host: ConfigurationHost }) {
  const standing = hostStanding(host)
  const identity = hostIdentity(host)
  return (
    <Stack gap={0}>
      <Text size="sm" fw={600}>{roleLabel(host.role)}</Text>
      <Text size="xs" c="dimmed" fw={400} ff={identity.isMachineName ? 'monospace' : undefined}>
        {identity.text}
      </Text>
      <Text size="xs" c={host.isStale ? 'yellow.8' : 'dimmed'} fw={400}>
        {host.processStartedAt
          ? `as of start, ${agoLabel(host.processStartedAt)}${host.isStale ? ' · stopped publishing' : ''}`
          : standing.label}
      </Text>
    </Stack>
  )
}

/** The setting's own cell: key, rationale, who binds it, its default, and — where rows differ — why it is locked. */
function SettingDescription({
  setting, columns, showNote,
}: { setting: ConfigurationSetting; columns: HostColumn[]; showNote: boolean }) {
  return (
    <Stack gap={6}>
      <SettingHeading setting={setting} />
      <Text size="sm">{setting.rationale}</Text>
      <Text size="xs" c="dimmed">
        Bound by {setting.boundBy}
        {/*
          No default on a Bootstrap row, and the server sends none: a default is a
          value, and a Bootstrap row shows no value of any kind.
        */}
        {setting.tier !== 'Bootstrap' && (
          <> · code default <Code>{setting.codeDefault ?? 'none'}</Code></>
        )}
      </Text>
      {showNote && (
        <Text size="xs" c="dimmed">
          <strong>{setting.tier === 'Locked' ? 'Why it is locked:' : 'Not editable here:'}</strong>{' '}
          {setting.editabilityNote}
        </Text>
      )}
      {setting.crossHostCheck && <CrossHostLine setting={setting} />}
      <UnplacedReadings setting={setting} columns={columns} />
    </Stack>
  )
}

function SettingHeading({ setting }: { setting: ConfigurationSetting }) {
  return (
    <Group gap={6} wrap="wrap">
      <Code style={{ overflowWrap: 'anywhere' }}>{setting.key}</Code>
      <TierBadge tier={setting.tier} />
      {setting.isReadByNoCode && (
        <Badge size="xs" variant="outline" color="gray">read by no code</Badge>
      )}
    </Group>
  )
}

/** The rule's verdict, repeated where the setting is listed so a failed rule is not only in the panel above. */
function CrossHostLine({ setting }: { setting: ConfigurationSetting }) {
  const check = setting.crossHostCheck!
  const verdict = crossHostStatus(check)
  return (
    <Group gap={6} wrap="nowrap" align="flex-start">
      <Standing status={verdict.status} label={verdict.label} isUnmeasured={verdict.status == null} />
      <Text size="xs" c="dimmed">{crossHostKindLabel(check.kind)}: {check.detail}</Text>
    </Group>
  )
}

/**
 * Any reading no host column claims. Expected never to render — it exists so a
 * drift between `hosts` and the observations prints a reading rather than
 * dropping one.
 */
function UnplacedReadings({ setting, columns }: { setting: ConfigurationSetting; columns: HostColumn[] }) {
  const unplaced = unplacedObservations(setting, columns)
  if (unplaced.length === 0) return null
  return (
    <Stack gap={4}>
      {unplaced.map((observation, i) => (
        <Group key={i} gap={6} wrap="nowrap" align="flex-start">
          <Text size="xs" fw={500}>{hostLabel({ ...observation, role: observation.hostRole })}:</Text>
          <ObservationCell observation={observation} />
        </Group>
      ))}
    </Stack>
  )
}

/**
 * One host's reading of one setting — three states, never alike.
 *
 *  - **No observation**: the host does not bind the key. Plain words, no dash,
 *    because a dash means unmeasured and this is a structural fact.
 *  - **Never reported**: a dash and the server's caption. Unmeasured, not off.
 *  - **Reported**: the server's `valueLabel`, and a source line that begins from
 *    process start. A shown value is set in the mono face so a canonical `false`
 *    is not confused with prose; every other label is prose.
 */
function ObservationCell({
  observation, align = 'left',
}: { observation: ConfigurationObservation | undefined; align?: 'left' | 'right' }) {
  if (!observation) {
    return <Text size="xs" c="dimmed" fs="italic" ta={align}>Not bound on this host</Text>
  }

  if (observation.hasNeverReported) {
    return (
      <Group gap={6} wrap="nowrap" align="flex-start" justify={align === 'right' ? 'flex-end' : 'flex-start'}>
        <Text span c="dimmed" aria-hidden="true">{NOT_MEASURED}</Text>
        <Text span size="xs" c="dimmed">{observation.valueLabel}</Text>
      </Group>
    )
  }

  const shown = observation.disposition === 'Shown'
  const source = sourceLabel(observation)

  return (
    <Stack gap={2} align={align === 'right' ? 'flex-end' : 'flex-start'}>
      <Text
        size="sm"
        ff={shown ? 'monospace' : undefined}
        fw={shown ? 500 : undefined}
        c={observation.disposition === 'NotApplicable' ? 'dimmed' : undefined}
        ta={align}
        style={{ overflowWrap: 'anywhere' }}
      >
        {observation.valueLabel}
      </Text>
      {source && <Text size="xs" c="dimmed" ta={align}>{source}</Text>}
      {/*
        Stale after the value, never instead of it: a host that stopped publishing
        still ran with these, and blanking them would lose the last known fact. The
        wording says what it is — the last reading, not a current one.
      */}
      {observation.isStale && (
        <Text size="xs" c="yellow.8" ta={align}>Stopped publishing — this is what it last ran with</Text>
      )}
    </Stack>
  )
}

/**
 * A status badge; or, with no status role, the caption alone — preceded by the
 * dash only when there is genuinely no reading behind it.
 *
 * `StatusBadge` takes a non-nullable role by design, and the union was
 * deliberately not widened. So "no role" arrives here as null, and it covers two
 * different claims: a known state that simply carries no colour ("Publishing")
 * and the absence of any reading ("Never reported", "Not judged"). Only the
 * second takes the dash. The first draft of this page gave both the dash, which
 * printed "— Read from this process" beside the one host that is always measured.
 */
function Standing({
  status, label, isUnmeasured,
}: { status: StatusRole | null; label: string; isUnmeasured: boolean }) {
  if (status) return <StatusBadge status={status} label={label} />
  return (
    <Group gap={6} wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
      {isUnmeasured && <Text span c="dimmed" aria-hidden="true">{NOT_MEASURED}</Text>}
      <Text span size="sm" c="dimmed">{label}</Text>
    </Group>
  )
}

/**
 * The tier, as a disclosure glyph: an open eye where a value is shown, a closed
 * one where only presence is, a lock where the value is shown and deliberately
 * not editable. Outlined and grey on purpose — a tier is not a severity, and a
 * coloured Bootstrap badge would read as a warning about every connection string.
 */
function TierBadge({ tier }: { tier: ConfigurationTier }) {
  return (
    <Badge size="xs" variant="outline" color="gray" leftSection={<TierIcon tier={tier} size={10} />}>
      {tier}
    </Badge>
  )
}

function TierIcon({ tier, size = 16 }: { tier: ConfigurationTier; size?: number }): ReactNode {
  switch (tier) {
    case 'Locked': return <IconLock size={size} />
    case 'Bootstrap': return <IconEyeOff size={size} />
    case 'Operational': return <IconEye size={size} />
    // An unrecognised tier gets no disclosure glyph: an open eye would claim a
    // value is shown, a closed one that it is withheld, and neither is known.
    default: return <IconHelpCircle size={size} />
  }
}
